import { ModuleDataSyncService } from '@application/persistence/ModuleDataSyncService';
import { DtsService, type DtsAgrege } from '@domain/fiscalite/DtsService';
import type { Bulletin } from '@shared/types/Bulletin';
import type { DeclarationDts, StatutDts } from '@shared/types/Dts';
import type { Salarie } from '@shared/types/Salarie';
import { uuid } from '@shared/utils/uuid';

const NAMESPACE = 'fiscalite.dts';
const LEGACY_KEY = 'nexxus.fiscalite.dts';

type DtsPayload = { items: DeclarationDts[] };

function nowIso(): string {
  return new Date().toISOString();
}

function actifSurTrimestre(
  items: readonly DeclarationDts[],
  trimestre: string,
): DeclarationDts | undefined {
  return items.find((d) => d.trimestre === trimestre && d.statut !== 'annulee');
}

function appliquerAgrege(base: DeclarationDts, agrege: DtsAgrege, maj_le: string): DeclarationDts {
  return {
    ...base,
    trimestre: agrege.trimestre,
    periodes: agrege.periodes,
    lignes: agrege.lignes,
    total_brut: agrege.total_brut,
    total_base_cnss: agrege.total_base_cnss,
    total_patronale: agrege.total_patronale,
    total_salariale: agrege.total_salariale,
    effectif: agrege.effectif,
    maj_le,
  };
}

export async function listDts(tenantId: string): Promise<DeclarationDts[]> {
  const data = await ModuleDataSyncService.getJson<DtsPayload>(
    tenantId,
    NAMESPACE,
    { items: [] },
    LEGACY_KEY,
  );
  return Array.isArray(data.items) ? data.items : [];
}

export async function saveDts(tenantId: string, items: DeclarationDts[]): Promise<void> {
  await ModuleDataSyncService.setJson(tenantId, NAMESPACE, { items }, LEGACY_KEY);
}

export async function getDts(tenantId: string, id: string): Promise<DeclarationDts | undefined> {
  const items = await listDts(tenantId);
  return items.find((d) => d.id === id);
}

export async function upsertDts(tenantId: string, dts: DeclarationDts): Promise<DeclarationDts> {
  const items = await listDts(tenantId);
  const index = items.findIndex((d) => d.id === dts.id);
  if (index >= 0) items[index] = dts;
  else items.push(dts);
  await saveDts(tenantId, items);
  return dts;
}

export async function creerDts(
  tenantId: string,
  bulletins: readonly Bulletin[],
  salaries: readonly Salarie[],
  trimestre: string,
  opts: { notes?: string; remplacerBrouillon?: boolean } = {},
): Promise<DeclarationDts> {
  const items = await listDts(tenantId);
  const existant = actifSurTrimestre(items, trimestre);
  if (existant?.statut === 'deposee') {
    throw new Error(`Une DTS déposée existe déjà pour ${trimestre}`);
  }
  if (existant?.statut === 'brouillon' && !opts.remplacerBrouillon) {
    throw new Error(`Un brouillon DTS existe déjà pour ${trimestre}`);
  }

  const agrege = DtsService.agreger(bulletins, salaries, trimestre);
  const instant = nowIso();

  if (existant?.statut === 'brouillon') {
    return upsertDts(tenantId, {
      ...appliquerAgrege(existant, agrege, instant),
      notes: opts.notes ?? existant.notes,
    });
  }

  return upsertDts(tenantId, {
    id: uuid('dts'),
    tenant_id: tenantId,
    statut: 'brouillon',
    notes: opts.notes,
    cree_le: instant,
    maj_le: instant,
    trimestre: agrege.trimestre,
    periodes: agrege.periodes,
    lignes: agrege.lignes,
    total_brut: agrege.total_brut,
    total_base_cnss: agrege.total_base_cnss,
    total_patronale: agrege.total_patronale,
    total_salariale: agrege.total_salariale,
    effectif: agrege.effectif,
  });
}

export async function regenererDts(
  tenantId: string,
  id: string,
  bulletins: readonly Bulletin[],
  salaries: readonly Salarie[],
): Promise<DeclarationDts> {
  const current = await getDts(tenantId, id);
  if (!current) throw new Error('Déclaration introuvable');
  if (current.statut !== 'brouillon') {
    throw new Error('Seule une DTS en brouillon peut être régénérée');
  }
  const agrege = DtsService.agreger(bulletins, salaries, current.trimestre);
  return upsertDts(tenantId, appliquerAgrege(current, agrege, nowIso()));
}

export async function mettreAJourDts(
  tenantId: string,
  id: string,
  patch: { notes?: string; reference_depot?: string },
): Promise<DeclarationDts> {
  const current = await getDts(tenantId, id);
  if (!current) throw new Error('Déclaration introuvable');
  if (current.statut === 'annulee') {
    throw new Error('Une DTS annulée ne peut plus être modifiée');
  }
  return upsertDts(tenantId, {
    ...current,
    notes: patch.notes,
    reference_depot: patch.reference_depot,
    maj_le: nowIso(),
  });
}

export async function deposerDts(
  tenantId: string,
  id: string,
  reference_depot?: string,
): Promise<DeclarationDts> {
  const current = await getDts(tenantId, id);
  if (!current) throw new Error('Déclaration introuvable');
  if (current.statut !== 'brouillon') {
    throw new Error('Seule une DTS en brouillon peut être déposée');
  }
  const instant = nowIso();
  return upsertDts(tenantId, {
    ...current,
    statut: 'deposee',
    reference_depot: reference_depot || current.reference_depot,
    date_depot: instant,
    maj_le: instant,
  });
}

/** Brouillon : retrait de la liste. Déposée : annulation logique. */
export async function supprimerDts(
  tenantId: string,
  id: string,
): Promise<{ id: string; statut: StatutDts | 'supprimee' }> {
  const items = await listDts(tenantId);
  const current = items.find((d) => d.id === id);
  if (!current) throw new Error('Déclaration introuvable');
  if (current.statut === 'annulee') {
    throw new Error('Cette DTS est déjà annulée');
  }
  if (current.statut === 'brouillon') {
    await saveDts(
      tenantId,
      items.filter((d) => d.id !== id),
    );
    return { id, statut: 'supprimee' };
  }
  const updated: DeclarationDts = { ...current, statut: 'annulee', maj_le: nowIso() };
  await upsertDts(tenantId, updated);
  return { id, statut: 'annulee' };
}
